/**
 * Google Business Profile — flagship service page.
 * Interactive, visual-first landing (Index AI standard): live search simulation,
 * profile anatomy mockup, 60-minute review recovery flow, visual process
 * timeline, competitor benchmark — all CSS/motion built, no stock imagery.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as Icons from 'lucide-react';
import {
  ArrowRight,
  Search,
  MapPin,
  Star,
  Phone,
  Mail,
  MessageCircle,
  Navigation,
  Globe,
  CalendarCheck,
  Camera,
  Clock,
  BellRing,
  PenLine,
  CheckCircle2,
  Check,
  ShieldCheck,
  BarChart3,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import SEO from '../components/SEO';
import HeroImage from '../components/HeroImage';
import { heroFor } from '../data/serviceImages';
import RelatedResources from '../components/RelatedResources';
import { resolveContent } from '../data/serviceContent';
import { detailFor } from '../data/serviceDetail';
import { useT } from '../i18n';

const SLUG = 'google-business-profile';

/* Official Google "G" — used inside the GBP profile mockup. */
function GoogleG({ className = 'w-3.5 h-3.5' }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

const Ico = ({ name, className = 'w-5 h-5' }) => {
  const Cmp = (name && Icons[name]) || Icons.Sparkles;
  return <Cmp className={className} aria-hidden="true" />;
};

function SectionHeader({ eyebrow, title, body, dark = false, center = false }) {
  return (
    <div className={`max-w-2xl ${center ? 'mx-auto text-center' : ''}`}>
      <p className={dark ? 'kx-eyebrow-dark' : 'kx-eyebrow'}>{eyebrow}</p>
      <h2 className={`mt-3 text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight ${dark ? 'text-white' : 'text-neutral-900'}`}>
        {title}
      </h2>
      {body && (
        <p className={`mt-4 text-[15.5px] leading-relaxed ${dark ? 'text-neutral-300' : 'text-neutral-600'}`}>{body}</p>
      )}
    </div>
  );
}

// Typewriter for the live search bar.
function useTypewriter(words) {
  const [text, setText] = useState('');
  useEffect(() => {
    let w = 0;
    let i = 0;
    let deleting = false;
    let t;
    const tick = () => {
      const word = words[w];
      if (!deleting) {
        i += 1;
        setText(word.slice(0, i));
        if (i === word.length) {
          deleting = true;
          t = setTimeout(tick, 2000);
          return;
        }
        t = setTimeout(tick, 55);
      } else {
        i -= 1;
        setText(word.slice(0, i));
        if (i === 0) {
          deleting = false;
          w = (w + 1) % words.length;
        }
        t = setTimeout(tick, 25);
      }
    };
    t = setTimeout(tick, 500);
    return () => clearTimeout(t);
  }, [words]);
  return text;
}

const Stars = ({ n = 5, className = 'w-3 h-3' }) => (
  <span className="inline-flex items-center gap-[1px]">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`${className} ${i < n ? 'text-amber-400 fill-amber-400' : 'text-neutral-300'}`}
      />
    ))}
  </span>
);

// ── Live Google search simulation ─────────────────────────────────────
function SearchSimulation({ lang }) {
  const queries = useMemo(
    () =>
      lang === 'es'
        ? ['mejor restaurante italiano cerca de mí', 'brunch abierto ahora', 'cena romántica en el centro']
        : ['best italian restaurant near me', 'brunch open now', 'romantic dinner downtown'],
    [lang]
  );
  const typed = useTypewriter(queries);

  const results = [
    {
      you: true,
      name: 'Casa Mía San José',
      rating: '4.9',
      stars: 5,
      reviews: '1,204',
      meta: lang === 'es' ? 'Mexicano · $$ · 0.4 mi' : 'Mexican · $$ · 0.4 mi',
      tag: lang === 'es' ? 'Tu restaurante' : 'Your restaurant',
    },
    {
      name: 'Trattoria Verde',
      rating: '4.2',
      stars: 4,
      reviews: '318',
      meta: lang === 'es' ? 'Italiano · $$ · 0.7 mi' : 'Italian · $$ · 0.7 mi',
    },
    {
      name: "Luigi's Kitchen",
      rating: '4.0',
      stars: 4,
      reviews: '186',
      meta: lang === 'es' ? 'Italiano · $ · 1.1 mi' : 'Italian · $ · 1.1 mi',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.6 }}
      className="relative"
      data-testid="gbp-search-simulation"
    >
      <div className="rounded-3xl bg-white shadow-[0_50px_140px_-40px_rgba(0,0,0,0.55)] ring-1 ring-white/10 overflow-hidden">
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-neutral-100 bg-neutral-50/80">
          <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
          <div className="ml-3 flex-1 min-w-0 h-6 rounded-full bg-white border border-neutral-200 px-3 flex items-center">
            <span className="text-[10px] text-neutral-400 truncate">google.com/search</span>
          </div>
        </div>
        <div className="p-5 sm:p-6">
          {/* Search bar with typewriter */}
          <div className="flex items-center gap-3 h-12 rounded-full border border-neutral-200 shadow-sm px-5 bg-white">
            <Search className="w-4 h-4 text-neutral-400 shrink-0" />
            <span className="text-[14px] text-neutral-800 truncate">
              {typed}
              <span className="inline-block w-[2px] h-4 bg-[#F77F11] ml-0.5 align-middle animate-pulse" />
            </span>
          </div>

          {/* Map strip */}
          <div className="mt-4 relative h-20 rounded-xl overflow-hidden bg-gradient-to-r from-emerald-50 via-sky-50 to-emerald-50">
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-40"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, rgba(120,160,130,0.25) 1px, transparent 1px), linear-gradient(rgba(120,160,130,0.25) 1px, transparent 1px)',
                backgroundSize: '34px 34px',
              }}
            />
            <div aria-hidden="true" className="absolute left-[18%] top-[52%] w-1.5 h-1.5 rounded-full bg-neutral-400" />
            <div aria-hidden="true" className="absolute left-[74%] top-[30%] w-1.5 h-1.5 rounded-full bg-neutral-400" />
            <motion.div
              initial={{ y: -8, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, type: 'spring', stiffness: 300 }}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-[70%]"
            >
              <MapPin className="w-8 h-8 text-[#F77F11] fill-[#F77F11]/20 drop-shadow" />
            </motion.div>
          </div>

          {/* Local pack results */}
          <div className="mt-4 space-y-2.5">
            {results.map((r, i) => (
              <motion.div
                key={r.name}
                initial={{ opacity: 0, x: -14 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.3 + i * 0.15 }}
                className={`rounded-xl border p-3.5 sm:p-4 transition-all ${
                  r.you
                    ? 'border-[#F77F11]/50 bg-orange-50/60 shadow-[0_16px_44px_-20px_rgba(247,127,17,0.45)]'
                    : 'border-neutral-200 bg-white'
                }`}
                data-testid={`gbp-sim-result-${i}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-bold text-[14.5px] tracking-[-0.005em] ${r.you ? 'text-neutral-900' : 'text-neutral-700'}`}>
                        {r.name}
                      </p>
                      {r.you && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#F77F11] text-white text-[9.5px] font-bold tracking-[0.08em] uppercase px-2 py-0.5">
                          #1 · {r.tag}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-[12px] text-neutral-500">
                      <span className="font-semibold text-neutral-700">{r.rating}</span>
                      <Stars n={r.stars} />
                      <span>({r.reviews})</span>
                      <span className="hidden sm:inline">· {r.meta}</span>
                    </div>
                    {r.you && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {[
                          lang === 'es' ? 'Reservar' : 'Reserve',
                          lang === 'es' ? 'Menú' : 'Menu',
                          lang === 'es' ? 'Cómo llegar' : 'Directions',
                          lang === 'es' ? 'Pedir' : 'Order',
                        ].map((chip) => (
                          <span key={chip} className="rounded-full border border-[#F77F11]/40 text-[#d8651a] bg-white text-[10.5px] font-semibold px-2.5 py-0.5">
                            {chip}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <Navigation className={`w-4 h-4 shrink-0 mt-1 ${r.you ? 'text-[#F77F11]' : 'text-neutral-300'}`} />
                </div>
              </motion.div>
            ))}
          </div>
          <p className="mt-4 text-[10.5px] text-neutral-400 text-center">
            {lang === 'es' ? 'Simulación ilustrativa del Map Pack de Google.' : 'Illustrative simulation of the Google Map Pack.'}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// ── Profile anatomy mockup ─────────────────────────────────────────────
function ProfileMockup({ lang }) {
  const actions = [
    { icon: Globe, label: lang === 'es' ? 'Sitio web' : 'Website' },
    { icon: Navigation, label: lang === 'es' ? 'Cómo llegar' : 'Directions' },
    { icon: Phone, label: lang === 'es' ? 'Llamar' : 'Call' },
    { icon: CalendarCheck, label: lang === 'es' ? 'Reservar' : 'Reserve' },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55 }}
      className="rounded-3xl border border-neutral-200 bg-white shadow-[0_40px_110px_-40px_rgba(0,0,0,0.25)] overflow-hidden"
      data-testid="gbp-profile-mockup"
    >
      {/* cover photo strip */}
      <div className="relative grid grid-cols-3 gap-[2px] h-28">
        {[
          'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/x65h96bi_1%20FOOD%20SIGUIENE%20SECCION.png',
          'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/nds6t73v_2%20segunda%20restaurant%20interior%20with%20people.png',
          'https://customer-assets.emergentagent.com/job_kitxens-redesign/artifacts/93phka50_3%20chef%20kitxens.png',
        ].map((src, i) => (
          <div key={i} className="relative overflow-hidden">
            <img src={src} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            {i === 2 && (
              <span className="absolute bottom-1.5 right-1.5 rounded-md bg-black/50 text-white text-[9px] font-semibold px-1.5 py-0.5">
                +32
              </span>
            )}
          </div>
        ))}
        <span className="absolute top-2 left-2 inline-flex items-center gap-1.5 rounded-full bg-white/95 shadow-sm px-2.5 py-1">
          <GoogleG />
          <span className="text-[10px] font-semibold text-neutral-600">Google</span>
        </span>
      </div>
      <div className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className="shrink-0 w-11 h-11 rounded-full bg-gradient-to-br from-[#B3261E] to-[#7a1a14] ring-2 ring-white shadow-md flex items-center justify-center text-white font-bold text-[13px] tracking-tight -mt-8 relative z-10">
              CM
            </span>
            <div className="min-w-0">
              <h3 className="text-[19px] font-bold text-neutral-900 tracking-[-0.01em]">Casa Mía San José</h3>
              <div className="mt-1 flex items-center gap-1.5 text-[12.5px] text-neutral-500">
                <span className="font-semibold text-neutral-800">4.8</span>
                <Stars n={5} />
                <span>(1,204)</span>
              </div>
              <p className="mt-1 text-[12px] text-neutral-500">
                {lang === 'es' ? 'Restaurante mexicano · $$ · Abierto ahora' : 'Mexican restaurant · $$ · Open now'}
              </p>
            </div>
          </div>
          <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 text-[10px] font-bold px-2.5 py-1">
            <GoogleG className="w-3 h-3" /> {lang === 'es' ? 'Verificado por Google' : 'Google Verified'}
          </span>
        </div>
        <div className="mt-4 grid grid-cols-4 gap-2">
          {actions.map((a) => (
            <div key={a.label} className="flex flex-col items-center gap-1.5 rounded-xl border border-neutral-200 py-2.5 hover:border-[#F77F11]/60 hover:bg-orange-50/40 transition-colors">
              <a.icon className="w-4 h-4 text-[#1A73E8]" />
              <span className="text-[10px] font-semibold text-neutral-600">{a.label}</span>
            </div>
          ))}
        </div>
        {/* latest post + review snippet */}
        <div className="mt-4 rounded-xl bg-neutral-50 border border-neutral-100 p-3.5">
          <p className="text-[10px] tracking-[0.16em] font-bold uppercase text-neutral-400">
            {lang === 'es' ? 'Publicación de esta semana' : "This week's post"}
          </p>
          <p className="mt-1 text-[12.5px] text-neutral-700 leading-relaxed">
            {lang === 'es'
              ? '🌮 Tacos de birria — solo este fin de semana. Reserva tu mesa.'
              : '🌮 Birria tacos — this weekend only. Book your table.'}
          </p>
        </div>
        <div className="mt-2.5 rounded-xl bg-neutral-50 border border-neutral-100 p-3.5">
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-[#F77F11]/15 text-[#d8651a] text-[10px] font-bold flex items-center justify-center">M</span>
            <Stars n={5} className="w-2.5 h-2.5" />
            <span className="text-[10px] text-neutral-400">{lang === 'es' ? 'hace 2 h · respondida' : '2h ago · replied'}</span>
          </div>
          <p className="mt-1.5 text-[12px] text-neutral-600 leading-relaxed italic">
            {lang === 'es' ? '"Los mejores tacos al pastor de la ciudad. Volvemos seguro."' : '"Best tacos al pastor in town. We\'ll definitely be back."'}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// ── 60-minute review recovery flow ────────────────────────────────────
function RecoveryFlow({ lang }) {
  const steps = [
    {
      icon: BellRing,
      time: lang === 'es' ? 'Minuto 0' : 'Minute 0',
      t: lang === 'es' ? 'Alerta instantánea' : 'Instant alert',
      b: lang === 'es'
        ? 'La reseña negativa dispara una alerta al duty manager con el contexto completo de la visita.'
        : 'The negative review triggers a duty-manager alert with the full context of the visit.',
    },
    {
      icon: PenLine,
      time: lang === 'es' ? 'Minuto 25' : 'Minute 25',
      t: lang === 'es' ? 'Respuesta redactada en tu voz' : 'Response drafted in your voice',
      b: lang === 'es'
        ? 'Reputation AI redacta la respuesta con tu contrato de voz calibrado. Un humano la aprueba.'
        : 'Reputation AI drafts the reply using your calibrated voice contract. A human approves it.',
    },
    {
      icon: CheckCircle2,
      time: lang === 'es' ? '< 60 minutos' : '< 60 minutes',
      t: lang === 'es' ? 'Publicada + oferta de recuperación' : 'Published + recovery offer',
      b: lang === 'es'
        ? 'Respuesta publicada y oferta de recuperación opcional enviada al huésped por su canal original.'
        : 'Reply published and an optional recovery offer sent to the guest via their original channel.',
    },
  ];
  return (
    <div className="grid lg:grid-cols-[1fr_1.15fr] gap-10 lg:gap-14 items-center">
      {/* the negative review card */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.5 }}
        className="rounded-2xl bg-white/[0.04] border border-white/10 p-6"
        data-testid="gbp-recovery-review"
      >
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-full bg-rose-500/20 text-rose-300 text-[12px] font-bold flex items-center justify-center">J</span>
          <div>
            <p className="text-white text-[13.5px] font-semibold">Jake R.</p>
            <div className="flex items-center gap-1.5">
              <Stars n={2} className="w-2.5 h-2.5" />
              <span className="text-[10.5px] text-neutral-500">{lang === 'es' ? 'hace 12 min' : '12 min ago'}</span>
            </div>
          </div>
        </div>
        <p className="mt-3 text-neutral-300 text-[13.5px] leading-relaxed italic">
          {lang === 'es'
            ? '"Esperamos 40 minutos por la mesa que habíamos reservado. La comida bien, pero salimos frustrados."'
            : '"We waited 40 minutes for the table we had booked. Food was fine but we left frustrated."'}
        </p>
        <div className="mt-4 rounded-xl bg-[#F77F11]/10 border border-[#F77F11]/30 p-4">
          <p className="text-[10px] tracking-[0.16em] font-bold uppercase text-[#F77F11]">
            {lang === 'es' ? 'Respuesta Kitxens — publicada en 47 min' : 'Kitxens reply — published in 47 min'}
          </p>
          <p className="mt-1.5 text-neutral-200 text-[12.5px] leading-relaxed">
            {lang === 'es'
              ? '"Jake, tienes toda la razón y lo sentimos. El sábado nos superó y no lo gestionamos bien. Te escribimos por el canal de tu reserva para compensarlo — nos encantaría recibirte de nuevo como mereces."'
              : '"Jake, you\'re absolutely right and we\'re sorry. Saturday got ahead of us and we didn\'t handle it well. We\'ve reached out via your booking channel to make it right — we\'d love to host you again the way you deserve."'}
          </p>
        </div>
      </motion.div>

      {/* the timeline */}
      <div className="relative">
        <div aria-hidden="true" className="absolute left-[22px] top-3 bottom-3 w-px bg-gradient-to-b from-[#F77F11] via-[#F77F11]/40 to-transparent" />
        <div className="space-y-8">
          {steps.map((s, i) => (
            <motion.div
              key={s.t}
              initial={{ opacity: 0, x: 16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.45, delay: i * 0.12 }}
              className="relative flex gap-5"
            >
              <span className="relative z-10 shrink-0 w-11 h-11 rounded-full bg-neutral-900 border border-[#F77F11]/60 flex items-center justify-center shadow-[0_0_30px_-6px_rgba(247,127,17,0.5)]">
                <s.icon className="w-4.5 h-4.5 w-[18px] h-[18px] text-[#F77F11]" />
              </span>
              <div className="min-w-0">
                <p className="text-[10.5px] tracking-[0.2em] font-bold uppercase text-[#F77F11]">{s.time}</p>
                <h3 className="mt-1 text-white font-semibold text-[16.5px] tracking-[-0.005em]">{s.t}</h3>
                <p className="mt-1.5 text-neutral-400 text-[13.5px] leading-relaxed">{s.b}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Competitor benchmark bars ──────────────────────────────────────────
function BenchmarkChart({ lang }) {
  const rows = [
    { label: lang === 'es' ? 'Tu restaurante' : 'Your restaurant', w: 100, you: true },
    { label: lang === 'es' ? 'Competidor A' : 'Competitor A', w: 72 },
    { label: lang === 'es' ? 'Competidor B' : 'Competitor B', w: 61 },
    { label: lang === 'es' ? 'Competidor C' : 'Competitor C', w: 48 },
    { label: lang === 'es' ? 'Competidor D' : 'Competitor D', w: 39 },
  ];
  return (
    <div className="rounded-3xl border border-neutral-200 bg-white p-6 sm:p-8 shadow-[0_30px_90px_-40px_rgba(0,0,0,0.2)]" data-testid="gbp-benchmark-chart">
      <div className="flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-[#F77F11]" />
        <p className="text-[11px] tracking-[0.2em] font-bold uppercase text-neutral-500">
          {lang === 'es' ? 'Impresiones de búsqueda · semana 24' : 'Search impressions · week 24'}
        </p>
      </div>
      <div className="mt-6 space-y-4">
        {rows.map((r, i) => (
          <div key={r.label}>
            <div className="flex items-center justify-between text-[12.5px] mb-1.5">
              <span className={`font-semibold ${r.you ? 'text-[#d8651a]' : 'text-neutral-600'}`}>{r.label}</span>
              <span className={`font-bold ${r.you ? 'text-[#F77F11]' : 'text-neutral-400'}`}>{r.w}</span>
            </div>
            <div className="h-2.5 rounded-full bg-neutral-100 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                whileInView={{ width: `${r.w}%` }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.9, delay: i * 0.1, ease: 'easeOut' }}
                className={`h-full rounded-full ${r.you ? 'bg-gradient-to-r from-[#F77F11] to-[#ffa94d]' : 'bg-neutral-300'}`}
              />
            </div>
          </div>
        ))}
      </div>
      <p className="mt-5 text-[10.5px] text-neutral-400">
        {lang === 'es' ? 'Índice ilustrativo — cada cliente recibe su benchmark real contra 5 competidores nombrados.' : 'Illustrative index — every client receives their real benchmark against 5 named competitors.'}
      </p>
    </div>
  );
}

export default function GoogleBusinessProfilePage() {
  const { lang } = useT();
  const data = resolveContent(SLUG, 'services');
  const detail = detailFor(SLUG);
  const t = data[lang] || data.en;
  const category = data.category[lang] || data.category.en;
  const hero = heroFor(SLUG);
  const dv = (node) => (node && (node[lang] || node.en)) || '';

  const useCases = detail?.useCases || [];
  const process = detail?.process || [];
  const outcomes = detail?.outcomes || [];
  const deepDive = detail?.deepDive || [];
  const faqs = detail?.faqs || [];

  const stats = [
    { k: '62%', v: lang === 'es' ? 'de los comensales revisa Google antes de decidir dónde comer' : 'of diners check Google before deciding where to eat' },
    { k: '76%', v: lang === 'es' ? 'de las búsquedas "cerca de mí" terminan en visita en 24 horas' : 'of "near me" searches end in a visit within 24 hours' },
    { k: '~44%', v: lang === 'es' ? 'de los clics en búsquedas locales se los lleva el Map Pack' : 'of local-search clicks go to the Map Pack' },
  ];

  const pageUrl = `https://www.kitxens.com/services/${SLUG}`;
  const description = `${t.tagline} ${t.intro}`.slice(0, 220);
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.kitxens.com/' },
          { '@type': 'ListItem', position: 2, name: 'Services', item: 'https://www.kitxens.com/services' },
          { '@type': 'ListItem', position: 3, name: t.title, item: pageUrl },
        ],
      },
      {
        '@type': 'Service',
        '@id': `${pageUrl}#service`,
        name: t.title,
        serviceType: category,
        description,
        areaServed: ['United States', 'Canada', 'Mexico', 'Spain', 'Latin America'],
        provider: { '@type': 'Organization', name: 'Kitxens', url: 'https://www.kitxens.com' },
        url: pageUrl,
      },
      {
        '@type': 'FAQPage',
        '@id': `${pageUrl}#faq`,
        mainEntity: faqs.map((f) => ({
          '@type': 'Question',
          name: dv(f.q),
          acceptedAnswer: { '@type': 'Answer', text: dv(f.a) },
        })),
      },
    ],
  };

  return (
    <div className="bg-white" data-testid="landing-google-business-profile">
      <SEO title={`${t.title} · Kitxens`} description={description} path={`/services/${SLUG}`} schema={schema} />

      {/* ═══════ HERO — standard service hero ═══════ */}
      <section className="pt-28 pb-12 lg:pt-32 lg:pb-16 bg-gradient-to-b from-orange-50/50 to-white">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7">
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                <Link to="/services" className="inline-flex items-center gap-1 text-[12px] tracking-[0.18em] font-semibold uppercase text-[#F77F11] hover:text-[#d8651a]">
                  ← {category}
                </Link>
                <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] text-neutral-900 leading-[1.05]">
                  {t.title}
                </h1>
                <p className="mt-5 text-xl sm:text-2xl text-neutral-700 font-semibold leading-snug max-w-2xl">{t.tagline}</p>
                <p className="mt-5 text-neutral-600 text-base sm:text-lg leading-relaxed max-w-2xl">{t.intro}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/#audit"
                    className="inline-flex items-center gap-1 bg-[#F77F11] hover:bg-[#d8651a] text-white h-12 px-7 rounded-full text-[15px] font-semibold shadow-sm hover:shadow-md transition-all"
                    data-testid="landing-cta-audit"
                  >
                    {lang === 'es' ? 'Auditoría GBP gratis' : 'Free GBP Audit'} <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                  <Link
                    to="/how-it-works"
                    className="inline-flex items-center gap-1 border border-neutral-300 hover:bg-neutral-50 text-neutral-800 h-12 px-7 rounded-full text-[15px] font-semibold"
                  >
                    {lang === 'es' ? 'Cómo funciona' : 'How It Works'} <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <HeroImage image={hero} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════ 1. THE SEARCH THAT DECIDES DINNER — live simulation ═══════ */}
      <section className="py-20 lg:py-28 bg-neutral-950 text-white relative overflow-hidden" data-testid="gbp-search-section">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 right-[10%] w-[700px] h-[500px] rounded-full blur-3xl opacity-[0.15]"
          style={{ background: 'radial-gradient(circle, #F77F11 0%, transparent 65%)' }}
        />
        <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="kx-eyebrow-dark">
              {lang === 'es' ? 'La búsqueda que decide la cena' : 'The search that decides dinner'}
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-[-0.02em] leading-[1.1]">
              {lang === 'es'
                ? 'Cada noche, tu próximo cliente escribe esto en Google.'
                : 'Every night, your next guest types this into Google.'}
            </h2>
            <p className="mt-5 text-neutral-300 text-[15.5px] leading-relaxed">
              {lang === 'es'
                ? 'El Map Pack — los tres resultados con mapa — captura la mayoría de la atención en búsquedas locales. O apareces ahí, o la reserva se la lleva el de al lado. Nuestro trabajo es que ese primer resultado seas tú, semana tras semana.'
                : 'The Map Pack — the three map results — captures most of the attention in local searches. Either you show up there, or the booking goes next door. Our job is making that first result you, week after week.'}
            </p>
            <div className="mt-8 space-y-4">
              {stats.map((s, i) => (
                <motion.div
                  key={s.k}
                  initial={{ opacity: 0, x: -14 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4"
                >
                  <span className="text-[#F77F11] text-2xl font-bold tracking-[-0.02em] shrink-0 w-20">{s.k}</span>
                  <p className="text-neutral-300 text-[13.5px] leading-snug">{s.v}</p>
                </motion.div>
              ))}
            </div>
            <p className="mt-4 text-[10.5px] text-neutral-500">
              {lang === 'es' ? 'Benchmarks del sector de búsqueda local — calibramos los tuyos en la auditoría gratuita.' : 'Local-search industry benchmarks — we calibrate yours in the free audit.'}
            </p>
          </div>
          <SearchSimulation lang={lang} />
        </div>
      </section>

      {/* ═══════ 2. PROFILE ANATOMY — mockup + pillars ═══════ */}
      <section className="py-20 lg:py-28 bg-white" data-testid="gbp-anatomy-section">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow={lang === 'es' ? 'Anatomía de un perfil que convierte' : 'Anatomy of a profile that converts'}
            title={lang === 'es' ? 'Cada píxel de tu perfil, operado con intención.' : 'Every pixel of your profile, operated with intent.'}
            body={
              lang === 'es'
                ? 'Un perfil no se "configura" — se opera. Estos son los seis sistemas que tocamos cada semana para que Google (y la capa IA que lee tu perfil) confíen en ti.'
                : 'A profile is not "set up" — it is operated. These are the six systems we touch weekly so Google (and the AI layer reading your profile) trust you.'
            }
            center
          />
          <div className="mt-14 grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14 items-start">
            <div className="lg:sticky lg:top-28">
              <ProfileMockup lang={lang} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {(t.pillars || []).map((p, i) => (
                <motion.div
                  key={p.eyebrow}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.4, delay: (i % 2) * 0.06 }}
                  className="group rounded-2xl border border-neutral-200 bg-white p-6 hover:border-[#F77F11] hover:shadow-[0_24px_70px_-30px_rgba(247,127,17,0.45)] hover:-translate-y-0.5 transition-all"
                >
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-orange-50 text-[#F77F11] text-[12px] font-bold group-hover:bg-[#F77F11] group-hover:text-white transition-colors">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="mt-4 text-[#F77F11] text-[11px] tracking-[0.2em] font-bold uppercase">{p.eyebrow}</p>
                  <p className="mt-2 text-neutral-700 text-[14px] leading-relaxed">{p.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ 3. REVIEW RECOVERY — dark simulation ═══════ */}
      <section className="py-20 lg:py-28 bg-neutral-950 text-white relative overflow-hidden" data-testid="gbp-recovery-section">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, #F77F11 1.2px, transparent 1.8px), radial-gradient(circle at 80% 70%, #F77F11 1.2px, transparent 1.8px)',
            backgroundSize: '140px 140px, 200px 200px',
          }}
        />
        <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <p className="kx-eyebrow-dark">
              {lang === 'es' ? 'Recuperación en 60 minutos' : 'The 60-minute recovery'}
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight">
              {lang === 'es'
                ? 'Una reseña negativa a las 21:40. Así responde Kitxens.'
                : 'A negative review at 9:40 PM. This is how Kitxens responds.'}
            </h2>
            <p className="mt-4 text-neutral-300 text-[15.5px] leading-relaxed">
              {lang === 'es'
                ? 'El 89% de los comensales lee las respuestas del dueño. Una negativa bien recuperada convence más que diez positivas sin responder.'
                : '89% of diners read owner responses. One well-recovered negative persuades more than ten unanswered positives.'}
            </p>
          </div>
          <div className="mt-14">
            <RecoveryFlow lang={lang} />
          </div>
        </div>
      </section>

      {/* ═══════ 4. USE CASES ═══════ */}
      <section className="py-20 lg:py-24 bg-neutral-50/60" data-testid="landing-use-cases">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow={lang === 'es' ? 'Qué operamos cada semana' : 'What we operate every week'}
            title={lang === 'es' ? 'Construido para tu operación real.' : 'Built for your real operation.'}
            body={
              lang === 'es'
                ? 'Estas son las palancas concretas que movemos desde la primera semana — con SLA, cadencia y dueño responsable.'
                : 'These are the concrete levers we move from week one — with SLAs, cadence and a named owner.'
            }
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {useCases.map((u, i) => (
              <motion.div
                key={dv(u.t)}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.07 }}
                whileHover={{ y: -3 }}
                className="group rounded-2xl border border-neutral-200 bg-white p-7 hover:border-orange-200 hover:shadow-[0_20px_60px_-30px_rgba(247,127,17,0.4)] transition-all"
              >
                <span className="inline-flex w-11 h-11 rounded-xl bg-orange-50 items-center justify-center group-hover:bg-[#F77F11] transition-colors">
                  <Ico name={u.i} className="w-5 h-5 text-[#F77F11] group-hover:text-white transition-colors" />
                </span>
                <h3 className="mt-5 text-neutral-900 font-semibold text-[17px] leading-snug tracking-[-0.005em]">{dv(u.t)}</h3>
                <p className="mt-2 text-neutral-600 text-[14.5px] leading-relaxed">{dv(u.b)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 5. PROCESS — vertical visual timeline ═══════ */}
      <section className="py-20 lg:py-28 bg-white" data-testid="landing-process">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow={lang === 'es' ? 'Cómo trabajamos' : 'How we work'}
            title={lang === 'es' ? 'Del diagnóstico al efecto compuesto.' : 'From audit to compounding.'}
            body={
              lang === 'es'
                ? 'Sin rehacer tu stack. Sin reemplazar a tu equipo. Un ciclo operativo que se repite y mejora cada semana.'
                : 'No rip-and-replace. No team disruption. An operating cycle that repeats and improves every week.'
            }
            center
          />
          <div className="relative mt-16 max-w-3xl mx-auto">
            <div
              aria-hidden="true"
              className="absolute left-[27px] sm:left-1/2 sm:-translate-x-1/2 top-2 bottom-2 w-[2px] bg-gradient-to-b from-[#F77F11] via-orange-200 to-orange-50"
            />
            <div className="space-y-10">
              {process.map((s, i) => {
                const left = i % 2 === 0;
                return (
                  <motion.div
                    key={dv(s.t) + i}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.45, delay: 0.05 }}
                    className={`relative flex gap-6 sm:gap-0 sm:grid sm:grid-cols-[1fr_56px_1fr] items-start`}
                    data-testid={`gbp-process-step-${i}`}
                  >
                    {/* node */}
                    <span className="order-first sm:order-none sm:col-start-2 relative z-10 shrink-0 w-14 h-14 rounded-full bg-white border-2 border-[#F77F11] text-[#F77F11] font-bold text-[15px] flex items-center justify-center shadow-[0_10px_30px_-10px_rgba(247,127,17,0.6)] justify-self-center">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div
                      className={`min-w-0 rounded-2xl border border-neutral-200 bg-white p-6 hover:border-[#F77F11]/60 hover:shadow-[0_24px_70px_-30px_rgba(247,127,17,0.4)] transition-all ${
                        left ? 'sm:col-start-1 sm:row-start-1 sm:text-right' : 'sm:col-start-3 sm:row-start-1'
                      }`}
                    >
                      <h3 className="text-neutral-900 font-bold text-[17px] tracking-[-0.005em]">{dv(s.t)}</h3>
                      <p className="mt-2 text-neutral-600 text-[14px] leading-relaxed">{dv(s.b)}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ 6. BENCHMARK ═══════ */}
      <section className="py-20 lg:py-24 bg-neutral-50/60" data-testid="gbp-benchmark-section">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="kx-eyebrow">
              {lang === 'es' ? 'Benchmark semanal' : 'Weekly benchmark'}
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-neutral-900 tracking-[-0.02em] leading-tight">
              {lang === 'es' ? 'Nunca contra "la media del sector". Contra tus 5 competidores, con nombre.' : 'Never against "industry averages". Against your 5 named competitors.'}
            </h2>
            <p className="mt-4 text-neutral-600 text-[15.5px] leading-relaxed">
              {lang === 'es'
                ? 'Cada lunes recibes dónde estás en impresiones, reseñas, rating, cadencia de posts y frescura de fotos — comparado con los cinco locales contra los que realmente compites por la misma mesa.'
                : 'Every Monday you know where you stand on impressions, reviews, rating, post cadence and photo freshness — against the five venues you actually compete with for the same table.'}
            </p>
            <ul className="mt-6 space-y-2.5">
              {(lang === 'es'
                ? ['Reporte semanal automatizado', 'Deep-dive mensual con el owner operativo', 'Revisión estratégica trimestral']
                : ['Automated weekly report', 'Monthly deep-dive with the operating owner', 'Quarterly strategic review']
              ).map((b) => (
                <li key={b} className="flex items-start gap-3 text-neutral-700">
                  <span className="mt-1 w-5 h-5 shrink-0 rounded-full bg-orange-50 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-[#F77F11]" />
                  </span>
                  <span className="text-[15px] leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <BenchmarkChart lang={lang} />
        </div>
      </section>

      {/* ═══════ 7. OUTCOMES ═══════ */}
      <section className="py-20 lg:py-24 bg-[#0F1117] text-white" data-testid="landing-outcomes">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow={lang === 'es' ? 'Resultados' : 'Outcomes'}
            title={lang === 'es' ? 'Lo que tu perfil logra cuando alguien lo opera de verdad.' : 'What your profile achieves when someone truly operates it.'}
            dark
          />
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-5">
            {outcomes.map((o, i) => (
              <motion.div
                key={dv(o.k) + i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-7 hover:bg-white/[0.06] hover:border-[#F77F11]/40 transition-all"
              >
                <p className="text-[#F77F11] text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-none">{dv(o.k)}</p>
                <p className="mt-3 text-neutral-300 text-[13.5px] leading-relaxed">{dv(o.v)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 8. DEEP DIVE ═══════ */}
      <section className="py-20 lg:py-24 bg-white" data-testid="landing-deep-dive">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow={lang === 'es' ? 'Todo lo que gestionamos' : 'Everything we manage'}
            title={lang === 'es' ? 'Cada capítulo, operado con rigor.' : 'Every chapter, operated with rigor.'}
            body={
              lang === 'es'
                ? 'No es un servicio genérico. Es una operativa completa que gestiona cada palanca que mueve la aguja para tu restaurante.'
                : 'This is not a generic service. It is a complete operating layer covering every lever that moves the needle for your restaurant.'
            }
          />
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {deepDive.map((d, i) => (
              <motion.div
                key={dv(d.t) + i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.35, delay: (i % 3) * 0.05 }}
                className="group rounded-2xl border border-neutral-200 bg-white p-6 hover:border-[#F77F11] hover:shadow-[0_20px_60px_-30px_rgba(247,127,17,0.35)] transition-all"
              >
                <span className="inline-flex w-10 h-10 rounded-lg bg-orange-50 items-center justify-center group-hover:bg-[#F77F11] transition-colors">
                  <Ico name={d.i} className="w-5 h-5 text-[#F77F11] group-hover:text-white transition-colors" />
                </span>
                <h3 className="mt-4 text-neutral-900 font-bold text-[16px] leading-snug tracking-[-0.005em]">{dv(d.t)}</h3>
                <p className="mt-2 text-neutral-600 text-[13.5px] leading-relaxed">{dv(d.b)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 9. BENEFITS + TECH ═══════ */}
      <section className="py-16 lg:py-20 bg-neutral-50/60">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="kx-eyebrow">{lang === 'es' ? 'Lo que vas a lograr' : "What you'll achieve"}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-neutral-900 leading-tight">
              {lang === 'es' ? 'Beneficios que se notan.' : 'Benefits you can feel.'}
            </h2>
            <ul className="mt-7 space-y-3">
              {(t.benefits || []).map((b) => (
                <li key={b} className="flex items-start gap-3 text-neutral-700">
                  <span className="mt-1 w-5 h-5 shrink-0 rounded-full bg-orange-50 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-[#F77F11]" />
                  </span>
                  <span className="text-base leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="kx-eyebrow">{lang === 'es' ? 'Tecnologías' : 'Technologies'}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-neutral-900 leading-tight">
              {lang === 'es' ? 'Con las plataformas que ya usas.' : 'With the platforms you already use.'}
            </h2>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {(t.tech || []).map((name) => (
                <span key={name} className="inline-flex items-center bg-white border border-neutral-200 rounded-full px-4 py-2 text-sm font-semibold text-neutral-700">
                  {name}
                </span>
              ))}
            </div>
            <div className="mt-8 rounded-2xl border border-orange-100 bg-orange-50/50 p-5">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#F77F11]" />
                <p className="text-[12px] font-bold tracking-[0.12em] uppercase text-[#d8651a]">
                  {lang === 'es' ? 'Tú siempre eres el dueño' : 'You always own the profile'}
                </p>
              </div>
              <p className="mt-2 text-[13.5px] text-neutral-700 leading-relaxed">
                {lang === 'es'
                  ? 'Operamos con acceso de manager, revocable en cualquier momento. La propiedad nunca se transfiere a Kitxens.'
                  : 'We operate under manager-level access, revocable at any moment. Ownership never transfers to Kitxens.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ 10. FAQs ═══════ */}
      <section className="py-16 lg:py-24" data-testid="landing-faq">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center">
            <p className="kx-eyebrow">
              {lang === 'es' ? 'Preguntas frecuentes' : 'Frequently asked'}
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-neutral-900 leading-tight">
              {lang === 'es' ? 'Lo que los operadores nos preguntan.' : 'What operators ask us.'}
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10 w-full divide-y divide-neutral-200 border-y border-neutral-200">
            {faqs.map((f, i) => (
              <AccordionItem key={`faq-${i}`} value={`faq-${i}`} className="border-b-0" data-testid={`landing-faq-item-${i}`}>
                <AccordionTrigger className="py-5 text-[16px] font-semibold text-neutral-900 hover:no-underline hover:text-[#F77F11] text-left">
                  {dv(f.q)}
                </AccordionTrigger>
                <AccordionContent className="pb-6 text-[15px] text-neutral-600 leading-relaxed">{dv(f.a)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ═══════ 11. RELATED ═══════ */}
      {data.related?.length > 0 && (
        <section className="py-16 lg:py-20 bg-neutral-50/60">
          <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
            <p className="kx-eyebrow">{lang === 'es' ? 'Servicios relacionados' : 'Related services'}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-neutral-900 leading-tight">
              {lang === 'es' ? 'Explora la plataforma Kitxens.' : 'Explore the Kitxens platform.'}
            </h2>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.related.map((r) => (
                <Link
                  key={r.href}
                  to={r.href}
                  className="group flex items-center justify-between gap-3 rounded-2xl border border-neutral-200 bg-white px-5 py-4 hover:border-[#F77F11] hover:bg-orange-50/40 transition-all"
                >
                  <span className="font-semibold text-neutral-900 text-[15px] group-hover:text-[#F77F11] transition-colors">{r.label}</span>
                  <ArrowRight className="w-4 h-4 text-neutral-400 group-hover:text-[#F77F11] group-hover:translate-x-1 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <RelatedResources serviceSlug={SLUG} lang={lang} />

      {/* ═══════ 12. CTA ═══════ */}
      <section className="py-20">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="rounded-3xl bg-[#F77F11] text-white p-8 sm:p-12 flex flex-col sm:flex-row sm:items-center justify-between gap-5 overflow-hidden relative">
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-[0.10]"
              style={{ backgroundImage: 'radial-gradient(circle at 25% 30%, #fff 1px, transparent 1.5px), radial-gradient(circle at 75% 70%, #fff 1px, transparent 1.5px)', backgroundSize: '110px 110px, 150px 150px' }}
            />
            <div className="relative">
              <p className="text-white/80 text-xs tracking-[0.22em] font-semibold uppercase">
                {lang === 'es' ? 'Auditoría GBP Gratis' : 'Free GBP Audit'}
              </p>
              <h3 className="mt-2 text-2xl sm:text-3xl font-bold leading-tight max-w-xl">
                {lang === 'es' ? '¿Listo para ganar el Map Pack de tu barrio?' : 'Ready to win the Map Pack in your neighborhood?'}
              </h3>
            </div>
            <div className="relative flex flex-wrap gap-3">
              <Link to="/#audit" className="inline-flex items-center gap-1 bg-white text-[#F77F11] hover:bg-neutral-100 h-12 px-6 rounded-full text-sm font-semibold" data-testid="landing-footer-cta-audit">
                {lang === 'es' ? 'Solicitar Auditoría' : 'Request Free Audit'} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact#contact-form" className="inline-flex items-center gap-1 bg-white/0 border border-white/40 hover:bg-white/10 text-white h-12 px-6 rounded-full text-sm font-semibold" data-testid="landing-footer-cta-talk">
                {lang === 'es' ? 'Hablar con un experto' : 'Talk to an expert'} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-neutral-600">
            <a href="https://wa.me/14085901102" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 hover:text-[#F77F11]"><MessageCircle className="w-4 h-4 text-[#25D366] group-hover:text-[#F77F11] transition-colors" /> WhatsApp <span className="text-[11px] text-neutral-400 group-hover:text-[#F77F11]/80 transition-colors">(ESP | ENG)</span></a>
            <a href="tel:+14155982310" className="inline-flex items-center gap-2 hover:text-[#F77F11]"><Phone className="w-4 h-4 text-[#F77F11]" /> +1 (415) 598-2310</a>
            <a href="mailto:hello@kitxens.com" className="inline-flex items-center gap-2 hover:text-[#F77F11]"><Mail className="w-4 h-4 text-[#F77F11]" /> hello@kitxens.com</a>
          </div>
        </div>
      </section>
    </div>
  );
}
